import { useState } from 'react';

export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface UseReaddyFormOptions {
  submitAddr: string;
  honeypotName: string;
}

export function useReaddyForm({ submitAddr, honeypotName }: UseReaddyFormOptions) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');

  const submit = async (form: HTMLFormElement) => {
    const fd = new FormData(form);
    const honeypotRaw = fd.get(honeypotName);
    const honeypot = typeof honeypotRaw === 'string' ? honeypotRaw.trim() : '';
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return true;
    }
    fd.delete(honeypotName);

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => {
        if (typeof value === 'string') body.append(key, value);
      });

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta || {}) as Record<string, unknown>;
      const serverMsg =
        (meta.message as string) ||
        (parsed?.message as string) ||
        (meta.detail as string) ||
        responseText ||
        '';
      const code = parsed?.code as string | undefined;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交失败，请稍后重试。',
        );
        return false;
      }

      setStatus('success');
      setFormError('');
      form.reset();
      return true;
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
      return false;
    }
  };

  const reset = () => {
    setStatus('idle');
    setFormError('');
  };

  return { status, formError, submit, reset };
}